import type { ComponentType, ReactNode } from 'react'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import ButtonBase from '@mui/material/ButtonBase'
import {
  WizardAiGoalAnalysisIcon,
  WizardFileEditLineIcon,
  WizardListViewIcon,
  WizardTodoLineIcon,
} from './CampaignWizardStepIcons'
import {
  CAMPAIGN_WIZARD_BODY_PADDING_X_PX,
  CAMPAIGN_WIZARD_BODY_PADDING_Y_PX,
  CAMPAIGN_WIZARD_CANVAS_BG,
  CAMPAIGN_WIZARD_CARD_BG,
  CAMPAIGN_WIZARD_CARD_BORDER,
  CAMPAIGN_WIZARD_SHELL_MAX_WIDTH_PX,
  CAMPAIGN_WIZARD_STEPPER_PADDING_PX,
  CAMPAIGN_WIZARD_STEPPER_TO_BODY_GAP_PX,
} from '../lib/campaignWizardLayout'

/** Four macro phases — step icons match Figma dev handoff (Remix line set). */
const PHASES: { label: string; Icon: ComponentType<SvgIconProps> }[] = [
  { label: 'Campaign Definition', Icon: WizardFileEditLineIcon },
  { label: 'AI Goal Analysis', Icon: WizardAiGoalAnalysisIcon },
  { label: 'LOI Generation', Icon: WizardTodoLineIcon },
  { label: 'Team & Launch', Icon: WizardListViewIcon },
]

const TRACK_BG = CAMPAIGN_WIZARD_CARD_BORDER

/** MUI Paper defaults to theme `surfacePaper` (#F8FAFC) — Figma cards are #FFFFFF. */
const WIZARD_CARD_PAPER_SX = {
  bgcolor: CAMPAIGN_WIZARD_CARD_BG,
  borderColor: CAMPAIGN_WIZARD_CARD_BORDER,
  boxShadow: 'none',
  backgroundImage: 'none',
} as const
/** Completed steps — Figma `b-primary-2` (node 4255:2973). */
const PAST_STEP = '#04183f'
/** Future phase icons on light gray circle */
const FUTURE_ICON = '#6b7280'

function phaseHref(campaignId: string, phaseIndex: number): string | null {
  switch (phaseIndex) {
    case 0:
      return `/campaigns/${campaignId}/setup`
    case 1:
      return `/campaigns/${campaignId}/goal-analysis`
    case 2:
      return `/campaigns/${campaignId}/lines-of-inquiry`
    case 3:
      return `/campaigns/${campaignId}/team-and-launch`
    default:
      return null
  }
}

type PhaseDef = (typeof PHASES)[number]

function phaseStepOpacity(active: boolean, isFuture: boolean): number {
  if (active) return 1
  if (isFuture) return 0.4
  return 1
}

function phaseIconColor(active: boolean, isPast: boolean): string {
  if (active || isPast) return '#ffffff'
  return FUTURE_ICON
}

function phaseLabelColor(active: boolean, isFuture: boolean): string {
  if (active) return 'primary.main'
  if (isFuture) return '#23262f'
  return PAST_STEP
}

function phaseCircleBg(active: boolean, isPast: boolean): string {
  if (active) return 'primary.main'
  if (isPast) return PAST_STEP
  return TRACK_BG
}

function phaseAriaLabel(
  base: string,
  clickable: boolean,
  active: boolean,
): string {
  if (clickable) return `Go to ${base}`
  if (active) return `${base} (current step)`
  return base
}

type WizardPhaseStepProps = {
  readonly phase: PhaseDef
  readonly index: number
  readonly safePhase: number
  readonly campaignId?: string
  readonly stepperNavigationLocked: boolean
  readonly goToPhase: (phaseIndex: number) => void
}

function WizardPhaseStep({
  phase,
  index,
  safePhase,
  campaignId,
  stepperNavigationLocked,
  goToPhase,
}: WizardPhaseStepProps) {
  const active = index === safePhase
  const isPast = index < safePhase
  const isFuture = index > safePhase
  const Icon = phase.Icon
  const clickable =
    !stepperNavigationLocked &&
    Boolean(campaignId && campaignId !== 'new') &&
    isPast
  const phaseAriaLabelText = phaseAriaLabel(
    phase.label,
    clickable,
    active,
  )

  return (
    <ButtonBase
      focusRipple={clickable}
      tabIndex={clickable ? 0 : -1}
      onClick={() => {
        if (clickable) goToPhase(index)
      }}
      aria-current={active ? 'step' : undefined}
      aria-label={phaseAriaLabelText}
      data-testid={`wizard-phase-${index}`}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'block',
        borderRadius: 2,
        py: 0.5,
        opacity: phaseStepOpacity(active, isFuture),
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable
          ? { opacity: 0.85, bgcolor: 'rgba(0, 140, 208, 0.04)' }
          : undefined,
      }}
    >
      <Stack alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: phaseCircleBg(active, isPast),
            flexShrink: 0,
          }}
        >
          <Icon
            sx={{
              fontSize: 24,
              color: phaseIconColor(active, isPast),
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            textAlign: 'center',
            color: phaseLabelColor(active, isFuture),
          }}
        >
          {phase.label}
        </Typography>
      </Stack>
    </ButtonBase>
  )
}

export function CampaignWizardChrome({
  children,
  /** 1-based step label in the header (e.g. API `wizardStep`). */
  wizardStep = 1,
  /** Total steps referenced in the header (Figma: four macro phases → "Step X of 4"). */
  totalWizardSteps = 4,
  /** 0-based index into {@link PHASES} — which macro phase is active. */
  activePhaseIndex = 0,
  /** When set, earlier stepper phases navigate back to completed wizard routes. */
  campaignId,
  /** When true, stepper phases are not clickable (e.g. LOI inline edit mode). */
  stepperNavigationLocked = false,
}: {
  readonly children: ReactNode
  readonly wizardStep?: number
  readonly totalWizardSteps?: number
  readonly activePhaseIndex?: number
  readonly campaignId?: string
  readonly stepperNavigationLocked?: boolean
}) {
  const navigate = useNavigate()
  const safePhase = Math.min(
    Math.max(activePhaseIndex, 0),
    PHASES.length - 1,
  )
  const progressPct = ((safePhase + 1) / PHASES.length) * 100

  const goToPhase = (phaseIndex: number) => {
    if (stepperNavigationLocked) return
    if (!campaignId || campaignId === 'new') return
    if (phaseIndex >= safePhase) return
    const href = phaseHref(campaignId, phaseIndex)
    if (href) navigate(href)
  }

  return (
    <Box
      data-testid="campaign-wizard-page"
      sx={{
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: CAMPAIGN_WIZARD_CANVAS_BG,
      }}
    >
      <Box
        data-testid="campaign-wizard-shell"
        sx={{
          width: '100%',
          maxWidth: `${CAMPAIGN_WIZARD_SHELL_MAX_WIDTH_PX}px`,
          minWidth: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          alignSelf: 'stretch',
          boxSizing: 'border-box',
          mx: 'auto',
        }}
      >
      <Paper
        elevation={0}
        variant="outlined"
        data-testid="campaign-wizard-stepper-card"
        sx={{
          ...WIZARD_CARD_PAPER_SX,
          p: `${CAMPAIGN_WIZARD_STEPPER_PADDING_PX}px`,
          mb: `${CAMPAIGN_WIZARD_STEPPER_TO_BODY_GAP_PX}px`,
          borderRadius: '10px',
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '-0.15px',
              color: '#23262f',
            }}
          >
            Create New Campaign
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              letterSpacing: '-0.15px',
              color: '#4a5565',
            }}
          >
            Step {wizardStep} of {totalWizardSteps}
          </Typography>
        </Stack>

        {/* Progress track — Figma: 8px pill, fill grows by macro phase */}
        <Box
          sx={{
            width: '100%',
            height: 8,
            borderRadius: 9999,
            bgcolor: TRACK_BG,
            mb: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${progressPct}%`,
              borderRadius: 9999,
              bgcolor: 'primary.main',
              transition: 'width 0.25s ease',
            }}
          />
        </Box>

        {/* Step icons — horizontal row; completed steps clickable to go back */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ gap: { xs: 1, sm: 2 } }}
        >
          {PHASES.map((phase, index) => (
            <WizardPhaseStep
              key={phase.label}
              phase={phase}
              index={index}
              safePhase={safePhase}
              campaignId={campaignId}
              stepperNavigationLocked={stepperNavigationLocked}
              goToPhase={goToPhase}
            />
          ))}
        </Stack>
      </Paper>
      <Paper
        elevation={0}
        data-testid="campaign-wizard-body-card"
        variant="outlined"
        sx={{
          ...WIZARD_CARD_PAPER_SX,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          px: `${CAMPAIGN_WIZARD_BODY_PADDING_X_PX}px`,
          py: `${CAMPAIGN_WIZARD_BODY_PADDING_Y_PX}px`,
          borderRadius: '10px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          data-testid="campaign-wizard-body-column"
          sx={{
            width: '100%',
            alignSelf: 'stretch',
            minWidth: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </Box>
      </Paper>
      </Box>
    </Box>
  )
}
